/**
 * Unified Pricing Workflow Service
 * Orchestrates the complete pricing workflow: Fetch → Calculate → Push to WebHotelier
 * Supports manual, automatic (on rule save), and scheduled triggers
 */

import { getWebHotelierPricingBridge } from '../../webhotelier/services/webHotelierPricingBridge';
import { registerResetHandler } from '../../webhotelier/services/webHotelierProxyService';
import { supabase } from '../../../services/supabaseClient';
import {
  handleDatabaseOperation,
  type ErrorHandlingResult,
} from '../../../utils/errorHandling';
import { addDays } from 'date-fns';
import { PRICING_CONFIG } from '../../../config/pricingConfig';

export type WorkflowTriggerType =
  | 'manual'
  | 'rule_save'
  | 'scheduled'
  | 'booking_event'
  | 'cancellation';

export type WorkflowStatus =
  | 'idle'
  | 'started'
  | 'fetching'
  | 'calculating'
  | 'pushing'
  | 'completed'
  | 'failed';

export type WorkflowOptions = {
  dryRun?: boolean;
  roomTypeId?: string;
  minConfidence?: number;
  maxPriceChange?: number;
  lookAheadDays?: number;
  /** Specific start date (overrides lookAheadDays if both provided) */
  startDate?: Date;
  /** Specific end date (overrides lookAheadDays if both provided) */
  endDate?: Date;
};

export type WorkflowResult = {
  success: boolean;
  executionId: string;
  triggerType: WorkflowTriggerType;
  triggerSource?: string;
  status: WorkflowStatus;
  startedAt: Date;
  completedAt?: Date;
  pricesFetched: number;
  pricesCalculated: number;
  pricesPushed: number;
  pricesSkipped: number;
  errors: Array<{ roomType: string; date: string; error: string }>;
  priceChanges: Array<{
    roomType: string;
    date: string;
    oldPrice: number;
    newPrice: number;
    adjustment: number;
  }>;
};

export type StatusCallback = (
  propertyId: string,
  status: WorkflowStatus,
  result?: Partial<WorkflowResult>
) => void;

export type WorkflowExecutionRecord = {
  id: string;
  property_id: string;
  trigger_type: WorkflowTriggerType;
  trigger_source: string | null;
  status: WorkflowStatus;
  started_at: string;
  completed_at: string | null;
  prices_fetched: number;
  prices_calculated: number;
  prices_pushed: number;
  prices_skipped: number;
  errors: Array<{ roomType: string; date: string; error: string }>;
  metadata: Record<string, unknown>;
};

class UnifiedPricingWorkflowService {
  private statusCallbacks = new Set<StatusCallback>();
  private currentStatus = new Map<string, WorkflowStatus>();
  private isExecuting = new Map<string, boolean>();

  subscribeToStatus(callback: StatusCallback): () => void {
    console.log(
      `[UnifiedPricingWorkflowService] New subscriber registered. Total: ${this.statusCallbacks.size + 1}`
    );
    this.statusCallbacks.add(callback);
    return () => {
      this.statusCallbacks.delete(callback);
      console.log(
        `[UnifiedPricingWorkflowService] Subscriber removed. Total: ${this.statusCallbacks.size}`
      );
    };
  }

  private emitStatus(
    propertyId: string,
    status: WorkflowStatus,
    result?: Partial<WorkflowResult>
  ): void {
    console.log(
      `[UnifiedPricingWorkflowService] emitStatus: ${status} for property ${propertyId}, subscribers: ${this.statusCallbacks.size}`
    );
    this.currentStatus.set(propertyId, status);
    this.statusCallbacks.forEach((cb) => cb(propertyId, status, result));
  }

  setExternalStatus(
    propertyId: string,
    status: WorkflowStatus,
    result?: Partial<WorkflowResult>
  ): void {
    console.log(
      `[UnifiedPricingWorkflowService] setExternalStatus called: ${status} for property ${propertyId}`
    );
    this.emitStatus(propertyId, status, result);
  }

  getWorkflowStatus(propertyId: string): WorkflowStatus {
    return this.currentStatus.get(propertyId) || 'idle';
  }

  isPropertyExecuting(propertyId: string): boolean {
    return this.isExecuting.get(propertyId) || false;
  }

  async executeWorkflow(
    propertyId: string,
    triggerType: WorkflowTriggerType,
    triggerSource?: string,
    options: WorkflowOptions = {}
  ): Promise<ErrorHandlingResult<WorkflowResult>> {
    return handleDatabaseOperation(async () => {
      if (this.isExecuting.get(propertyId)) {
        // Instead of throwing, return a skipped result
        // The running workflow will already process the latest data
        console.log(
          `[UnifiedPricingWorkflow] Skipping ${triggerType} trigger - workflow already executing for property ${propertyId}`
        );
        return {
          success: true,
          executionId: `skipped-${Date.now()}`,
          triggerType,
          triggerSource,
          status: 'completed' as const,
          startedAt: new Date(),
          completedAt: new Date(),
          pricesFetched: 0,
          pricesCalculated: 0,
          pricesPushed: 0,
          pricesSkipped: 0,
          errors: [],
          priceChanges: [],
        } satisfies WorkflowResult;
      }

      this.isExecuting.set(propertyId, true);
      const startedAt = new Date();
      const executionId = `wf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const result: WorkflowResult = {
        success: false,
        executionId,
        triggerType,
        triggerSource,
        status: 'fetching',
        startedAt,
        pricesFetched: 0,
        pricesCalculated: 0,
        pricesPushed: 0,
        pricesSkipped: 0,
        errors: [],
        priceChanges: [],
      };

      let dbExecutionId: string | null = null;

      try {
        dbExecutionId = await this.logWorkflowStart(
          executionId,
          propertyId,
          triggerType,
          triggerSource
        );

        this.emitStatus(propertyId, 'fetching', result);

        const pricingBridge = getWebHotelierPricingBridge();
        const {
          dryRun = false,
          minConfidence = PRICING_CONFIG.DEFAULT_MIN_CONFIDENCE,
          maxPriceChange = PRICING_CONFIG.DEFAULT_MAX_PRICE_CHANGE,
          lookAheadDays = PRICING_CONFIG.DEFAULT_LOOK_AHEAD_DAYS,
        } = options;

        // Use specific dates if provided, otherwise calculate from lookAheadDays
        const startDate = options.startDate || new Date();
        const endDate = options.endDate || addDays(startDate, lookAheadDays);

        this.emitStatus(propertyId, 'calculating', result);

        const executionResult = await pricingBridge.executePricingStrategy(
          propertyId,
          startDate,
          endDate,
          {
            dryRun,
            roomTypeId: options.roomTypeId,
            minConfidence,
            maxPriceChange,
          }
        );

        if (!executionResult.success || !executionResult.data) {
          throw Object.assign(
            new Error(
              executionResult.error?.message || 'Pricing execution failed'
            ),
            { details: executionResult.error?.details }
          );
        }

        const execData = executionResult.data;

        result.pricesFetched = execData.processedRooms;
        result.pricesCalculated = execData.priceChanges.length;
        result.pricesPushed = execData.updatedRates;
        result.pricesSkipped =
          execData.priceChanges.length - execData.updatedRates;
        result.errors = execData.errors;
        result.priceChanges = execData.priceChanges;
        result.success = execData.success;
        result.status = execData.success ? 'completed' : 'failed';
        result.completedAt = new Date();

        this.emitStatus(propertyId, result.status, result);

        if (dbExecutionId) {
          await this.logWorkflowComplete(dbExecutionId, result);
        }

        return result;
      } catch (error) {
        result.status = 'failed';
        result.completedAt = new Date();
        result.errors.push({
          roomType: 'all',
          date: new Date().toISOString(),
          error: error instanceof Error ? error.message : 'Unknown error',
        });

        this.emitStatus(propertyId, 'failed', result);
        if (dbExecutionId) {
          await this.logWorkflowComplete(dbExecutionId, result);
        }

        throw error;
      } finally {
        this.isExecuting.set(propertyId, false);
      }
    }, `Execute pricing workflow for property ${propertyId}`);
  }

  async triggerManual(
    propertyId: string,
    options?: WorkflowOptions
  ): Promise<ErrorHandlingResult<WorkflowResult>> {
    return this.executeWorkflow(propertyId, 'manual', 'user_action', options);
  }

  async triggerOnRuleSave(
    propertyId: string,
    ruleId: string,
    options?: WorkflowOptions
  ): Promise<ErrorHandlingResult<WorkflowResult>> {
    return this.executeWorkflow(
      propertyId,
      'rule_save',
      `rule:${ruleId}`,
      options
    );
  }

  async triggerScheduled(
    propertyId: string,
    scheduleId: string,
    options?: WorkflowOptions
  ): Promise<ErrorHandlingResult<WorkflowResult>> {
    return this.executeWorkflow(
      propertyId,
      'scheduled',
      `schedule:${scheduleId}`,
      options
    );
  }

  private async logWorkflowStart(
    executionId: string,
    propertyId: string,
    triggerType: WorkflowTriggerType,
    triggerSource?: string
  ): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('pricing_workflow_executions')
        .insert({
          property_id: propertyId,
          trigger_type: triggerType,
          trigger_source: triggerSource || null,
          status: 'started',
          started_at: new Date().toISOString(),
          metadata: { execution_ref: executionId },
        })
        .select('id')
        .single();

      if (error) {
        console.error(
          '[UnifiedPricingWorkflow] Failed to log workflow start:',
          error.message
        );
        return null;
      }

      return data?.id || null;
    } catch (error) {
      console.error(
        '[UnifiedPricingWorkflow] Failed to log workflow start:',
        error
      );
      return null;
    }
  }

  private async logWorkflowComplete(
    executionId: string,
    result: WorkflowResult
  ): Promise<void> {
    try {
      await supabase
        .from('pricing_workflow_executions')
        .update({
          status: result.status,
          completed_at: result.completedAt?.toISOString(),
          prices_fetched: result.pricesFetched,
          prices_calculated: result.pricesCalculated,
          prices_pushed: result.pricesPushed,
          prices_skipped: result.pricesSkipped,
          errors: result.errors,
          metadata: {
            priceChanges: result.priceChanges.slice(0, 100),
          },
        })
        .eq('id', executionId);
    } catch (error) {
      console.error(
        '[UnifiedPricingWorkflow] Failed to log workflow complete:',
        error
      );
    }
  }

  async getExecutionHistory(
    propertyId: string,
    limit = 10
  ): Promise<ErrorHandlingResult<WorkflowExecutionRecord[]>> {
    return handleDatabaseOperation(async () => {
      const { data, error } = await supabase
        .from('pricing_workflow_executions')
        .select('*')
        .eq('property_id', propertyId)
        .order('started_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    }, `Get execution history for property ${propertyId}`);
  }

  async getLastExecution(
    propertyId: string
  ): Promise<ErrorHandlingResult<WorkflowExecutionRecord | null>> {
    return handleDatabaseOperation(async () => {
      const { data, error } = await supabase
        .from('pricing_workflow_executions')
        .select('*')
        .eq('property_id', propertyId)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data || null;
    }, `Get last execution for property ${propertyId}`);
  }
}

let unifiedPricingWorkflowService: UnifiedPricingWorkflowService | null = null;

registerResetHandler(() => {
  unifiedPricingWorkflowService = null;
});

export function getUnifiedPricingWorkflowService(): UnifiedPricingWorkflowService {
  if (!unifiedPricingWorkflowService) {
    unifiedPricingWorkflowService = new UnifiedPricingWorkflowService();
  }
  return unifiedPricingWorkflowService;
}

export { UnifiedPricingWorkflowService };
